import type { CollectorIssue, LimitSnapshot, ToolId } from "../types";

/**
 * The wire format each machine publishes to the dead drop.
 *
 * This module is deliberately **isomorphic** — no `node:*` imports, no SQLite,
 * no crypto. The exporting machine (Node) and the reading browser both parse the
 * same shape with the same code, so a field can never mean two things. Encryption
 * wraps the bytes this produces; it is not part of the format.
 */

/**
 * Bumped whenever the shape changes incompatibly. A reader that doesn't
 * recognise the version refuses the blob rather than guessing at it: a partially
 * understood payload would render as confidently as a correct one.
 */
export const BLOB_FORMAT_VERSION = 1;

/**
 * One day's token totals for one tool and model.
 *
 * Aggregate, never raw events — this is the decision the whole design rests on.
 * Raw events run ~100/day, which is ~5 MB/year and breaks D1's 2 MB per-value
 * ceiling inside five months. Rolled up to day × tool × model it is ~240 KB/year
 * uncompressed, a few tens of KB gzipped. Full-resolution events stay in each
 * machine's local SQLite, which is the durable record anyway.
 *
 * Token counts, never costs: cost is derived at read time from current prices, so
 * a price correction repairs history without anyone re-exporting.
 */
export interface DailyUsageRow {
  /** Local calendar day, YYYY-MM-DD, as resolved on the exporting machine. */
  day: string;
  tool: ToolId;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheCreate5mTokens: number;
  cacheCreate1hTokens: number;
  cacheReadTokens: number;
}

/**
 * Everything about a tool that is *current state* rather than history.
 *
 * The rolling-window token counts are computed on the exporting machine, where
 * full event resolution exists, rather than re-derived from `DailyUsageRow`s.
 * Daily buckets cannot answer "the last 168 hours" without smearing the boundary
 * day, and cannot answer "the last 5 hours" at all. Carrying the numbers costs a
 * few bytes and keeps them exact as of the export.
 */
export interface ToolState {
  tool: ToolId;
  /** Live quota windows as last measured by the exporting machine. */
  limits: LimitSnapshot[];
  /** When this tool's data last landed on the exporting machine. */
  syncedAt: number | null;
  /** Why its latest collect couldn't complete, if it couldn't. */
  issue: CollectorIssue | null;
  /** Most recent usage event, epoch ms. Kept exact — a day bucket would round it. */
  lastEventTs: number | null;
  last5hTokens: number;
  last7dTokens: number;
}

/**
 * One machine's published snapshot for one calendar year.
 *
 * Chunked by year so a single stored value stays far below D1's 2 MB cap
 * regardless of how long the dashboard runs. At present rates one year is a few
 * tens of KB, so this is insurance rather than a live constraint — but the key
 * has to exist from the start or introducing it later is a migration.
 *
 * `tools` is repeated in every year's blob rather than living only in the current
 * one. It costs a few hundred bytes and means the reader never depends on which
 * years happened to be pushed, or in what order.
 */
export interface UsageBlob {
  v: number;
  /** Stable id of the producing machine (e.g. "personal-pc", "work-laptop"). */
  machine: string;
  /** Local calendar year the `daily` rows cover. */
  year: number;
  /** When this export was produced, epoch ms. */
  exportedAt: number;
  daily: DailyUsageRow[];
  tools: ToolState[];
}

/** Thrown when bytes can't be read as a blob this build understands. */
export class BlobFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BlobFormatError";
  }
}

/**
 * Serialize a blob to gzipped UTF-8 JSON.
 *
 * Gzip is applied before encryption, which is the only order that works:
 * ciphertext is indistinguishable from random and does not compress. It also
 * makes the JSON key names essentially free, so the format keeps readable field
 * names instead of the usual one-letter abbreviations — a blob that can be
 * eyeballed after a `gunzip` is worth more than the bytes.
 *
 * `CompressionStream` is a web standard present in both modern browsers and Node
 * 18+, so this one implementation serves the pusher and the reader alike.
 */
export async function encodeBlob(blob: UsageBlob): Promise<Uint8Array> {
  const json = new TextEncoder().encode(JSON.stringify(blob));
  return gzip(json);
}

/** Parse gzipped UTF-8 JSON back into a blob, validating what it must. */
export async function decodeBlob(bytes: Uint8Array): Promise<UsageBlob> {
  let text: string;
  try {
    text = new TextDecoder().decode(await gunzip(bytes));
  } catch (err) {
    throw new BlobFormatError(`could not decompress blob: ${(err as Error).message}`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new BlobFormatError("blob is not valid JSON");
  }
  return validateBlob(parsed);
}

/**
 * Check every field the reader would otherwise trust blindly.
 *
 * This is not schema validation for its own sake: the payload arrives from
 * storage the dashboard does not control, and a blob that is merely *plausible*
 * would be merged into the totals and rendered as fact. The element shapes are
 * checked, not just the envelope — a `day` that isn't a string throws inside the
 * merge sort, and a token count that isn't a number renders as `$NaN` with no
 * indication anything went wrong.
 *
 * A bad row rejects the **whole blob** rather than being dropped. Silently
 * discarding rows would leave the dashboard showing a confident subtotal of
 * unknown completeness, which is worse than showing nothing: the producer is
 * broken either way, and only one of those two outcomes says so.
 */
export function validateBlob(value: unknown): UsageBlob {
  const b = asObject(value, "blob");

  if (b.v !== BLOB_FORMAT_VERSION) {
    throw new BlobFormatError(
      `unsupported blob version ${String(b.v)} (this build reads ${BLOB_FORMAT_VERSION})`,
    );
  }
  if (typeof b.machine !== "string" || b.machine === "") {
    throw new BlobFormatError("blob has no machine id");
  }
  if (typeof b.year !== "number" || !Number.isInteger(b.year)) {
    throw new BlobFormatError("blob has no valid year");
  }
  if (!isCount(b.exportedAt)) {
    throw new BlobFormatError("blob has no valid exportedAt");
  }
  if (!Array.isArray(b.daily)) throw new BlobFormatError("blob.daily is not an array");
  if (!Array.isArray(b.tools)) throw new BlobFormatError("blob.tools is not an array");

  b.daily.forEach(validateDailyRow);
  b.tools.forEach(validateToolState);

  return b as unknown as UsageBlob;
}

const TOKEN_FIELDS = [
  "inputTokens",
  "outputTokens",
  "cacheCreate5mTokens",
  "cacheCreate1hTokens",
  "cacheReadTokens",
] as const;

const WINDOWS = ["5h", "weekly", "monthly"];

function validateDailyRow(value: unknown, i: number): void {
  const r = asObject(value, `daily[${i}]`);
  // The day string is both a sort key and a chart bucket key, so its shape is
  // load-bearing rather than cosmetic.
  if (typeof r.day !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(r.day)) {
    throw new BlobFormatError(`daily[${i}].day is not a YYYY-MM-DD string`);
  }
  if (typeof r.tool !== "string" || r.tool === "") {
    throw new BlobFormatError(`daily[${i}].tool is not a tool id`);
  }
  if (r.model !== null && typeof r.model !== "string") {
    throw new BlobFormatError(`daily[${i}].model is not a string or null`);
  }
  for (const f of TOKEN_FIELDS) {
    if (!isCount(r[f])) {
      throw new BlobFormatError(`daily[${i}].${f} is not a non-negative number`);
    }
  }
}

function validateToolState(value: unknown, i: number): void {
  const s = asObject(value, `tools[${i}]`);
  if (typeof s.tool !== "string" || s.tool === "") {
    throw new BlobFormatError(`tools[${i}].tool is not a tool id`);
  }
  if (!isCount(s.last5hTokens) || !isCount(s.last7dTokens)) {
    throw new BlobFormatError(`tools[${i}] has non-numeric rolling token counts`);
  }
  if (!isTimestampOrNull(s.syncedAt)) {
    throw new BlobFormatError(`tools[${i}].syncedAt is not a timestamp or null`);
  }
  if (!isTimestampOrNull(s.lastEventTs)) {
    throw new BlobFormatError(`tools[${i}].lastEventTs is not a timestamp or null`);
  }
  if (s.issue !== null && typeof s.issue !== "object") {
    throw new BlobFormatError(`tools[${i}].issue is not an object or null`);
  }
  if (!Array.isArray(s.limits)) {
    throw new BlobFormatError(`tools[${i}].limits is not an array`);
  }
  s.limits.forEach((l, j) => validateLimit(l, `tools[${i}].limits[${j}]`));
}

/** Limits are rendered directly as meters, so every field here reaches a pixel. */
function validateLimit(value: unknown, at: string): void {
  const l = asObject(value, at);
  if (typeof l.window !== "string" || !WINDOWS.includes(l.window)) {
    throw new BlobFormatError(`${at}.window is not one of ${WINDOWS.join("/")}`);
  }
  if (typeof l.label !== "string") throw new BlobFormatError(`${at}.label is not a string`);
  if (typeof l.unit !== "string") throw new BlobFormatError(`${at}.unit is not a string`);
  if (!isCount(l.used)) throw new BlobFormatError(`${at}.used is not a non-negative number`);
  if (l.limit !== null && !isCount(l.limit)) {
    throw new BlobFormatError(`${at}.limit is not a non-negative number or null`);
  }
  if (!isTimestampOrNull(l.resetsAt)) {
    throw new BlobFormatError(`${at}.resetsAt is not a timestamp or null`);
  }
  if (!isCount(l.capturedAt)) throw new BlobFormatError(`${at}.capturedAt is not a timestamp`);
}

function asObject(value: unknown, at: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new BlobFormatError(`${at} is not an object`);
  }
  return value as Record<string, unknown>;
}

/** Finite and non-negative — counts and timestamps are never either. */
function isCount(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}

function isTimestampOrNull(v: unknown): boolean {
  return v === null || isCount(v);
}

async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  return through(bytes, new CompressionStream("gzip"));
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  return through(bytes, new DecompressionStream("gzip"));
}

/** Push bytes through a transform stream and collect the result. */
async function through(
  bytes: Uint8Array,
  transform: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(transform);
  const chunks: Uint8Array[] = [];
  let total = 0;
  for await (const chunk of stream as unknown as AsyncIterable<Uint8Array>) {
    chunks.push(chunk);
    total += chunk.length;
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
